import { useCallback, useRef } from 'react'
import type { GetTokenSilentlyOptions, IdToken } from '@auth0/auth0-react'
import { useAppStore } from '@/store/appStore'
import { getIdentityByAuth0Sub, getEffectiveAccess, getMeContext, syncPartyRoleProducts } from './api'
import { FALLBACK_RECAUDOS_PRODUCT, isKsecEnabled } from './fallback'
import type { AccessAction } from './types'
import { getAuth0TokenOptions } from '@/lib/auth0Config'

type GetTokenFn = (options?: GetTokenSilentlyOptions) => Promise<string>
type GetIdTokenClaimsFn = () => Promise<IdToken | undefined>

// Dominancia de acciones KSEC: ALL > RW > R/W > NONE
const ACTION_RANK: Record<string, number> = { NONE: -1, R: 1, W: 1, RW: 2, ALL: 3 }

function buildAccessMap(
  entries: Array<{ resource?: { tagCode?: string | null } | null; action?: string | null }>,
): Record<string, AccessAction> {
  const map: Record<string, AccessAction> = {}
  for (const entry of entries) {
    const tag = entry.resource?.tagCode
    const action = entry.action as AccessAction | undefined
    if (!tag || !action) continue
    const existingRank = ACTION_RANK[map[tag]] ?? -Infinity
    const newRank = ACTION_RANK[action] ?? 0
    if (newRank > existingRank) {
      map[tag] = action
    }
  }
  return map
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useBootstrap() {
  const store = useAppStore()
  const runningRef = useRef(false)

  const bootstrap = useCallback(
    async (
      getAccessTokenSilently: GetTokenFn,
      getIdTokenClaims: GetIdTokenClaimsFn,
      auth0User: Record<string, unknown> | null | undefined,
    ) => {
      if (store.access.loaded || runningRef.current) return
      runningRef.current = true

      try {
        // ── Step 1: Tokens from Auth0 cache (no network round-trip) ──────────
        const tokenOpts = getAuth0TokenOptions()
        const [jwt, idTokenClaims] = await Promise.all([
          getAccessTokenSilently(tokenOpts),
          getIdTokenClaims(),
        ])

        const idToken = idTokenClaims?.__raw ?? ''
        if (!jwt) throw new Error('[Bootstrap] Access token not found.')
        if (!idToken) throw new Error('[Bootstrap] ID token not found.')

        store.setAuth({ jwt, idToken })

        // ── KSEC deshabilitado → modo degradado ──────────────────────────────
        // No se llama a KSEC (evita esperar los timeouts si el servicio está
        // caído) y se muestra Recaudos por defecto. El access map queda vacío,
        // así que el sidebar solo expone los items alwaysVisible.
        if (!isKsecEnabled()) {
          console.warn('[Bootstrap] KSEC deshabilitado (IT_MFE_KSEC_ENABLE) — mostrando producto por defecto: Recaudos.')
          store.setContextProducts([FALLBACK_RECAUDOS_PRODUCT])
          store.setAccessMap({}, true)
          return
        }

        // ── Step 2: Resolve KSEC Digital Identity via Auth0 sub (v3.0) ──────────
        // /by-email fue eliminado en v3.0. Usamos by-auth0-sub como entrada.
        const sub = idTokenClaims?.sub as string | undefined
        if (!sub) throw new Error('[Bootstrap] No sub claim in Auth0 ID token.')

        const email =
          (idTokenClaims?.email as string | undefined) ??
          (auth0User?.email as string | undefined) ??
          ''

        const identRes = await getIdentityByAuth0Sub(sub)
        // v3.0: respuesta directa { data: KsecDigitalIdentity }, NO anidada en { data: { identity } }
        const di = identRes.data

        store.setIdentity({
          // En v3.0 usamos DI.public_id para TODAS las llamadas KSEC (x-kbrm-party-id, effective-access, me/context)
          partyId:            di.id,
          ksecId:             di.id,
          individualPublicId: di.individualIdentified ?? '',
          // subjectPartyRolePublicId: mapeado desde kbrm_party_role_ref en el backend (migración 12)
          partyRolePublicId:  di.subjectPartyRolePublicId ?? '',
          email,
          nickname:           di.nickname ?? '',
          status:             di.status,
        })

        // ── Step 3: Load effective access + org context in parallel ───────────
        const [accessRes, ctxRes] = await Promise.all([
          getEffectiveAccess(di.id),
          getMeContext(di.id),
        ])

        const accessMap = buildAccessMap(accessRes.data.access ?? [])
        // ?? false: el campo puede venir undefined en respuestas de usuarios normales
        store.setIdentity({ isKashioSuper: accessRes.data.isKashioSuper ?? false })
        store.setAccessMap(accessMap, true)

        const ctx = ctxRes.data
        const tenants = ctx.tenants ?? []
        const firstTenant = tenants[0] ?? null

        store.setOrg({
          current: firstTenant
            ? {
                orgPartyPublicId: firstTenant.businessTenantPublicId,
                orgName: null,  // v3.0 no devuelve orgName; se obtendría de KBRM
                businessTenantId: firstTenant.businessTenantPublicId,
              }
            : null,
          visibleTenants: tenants,
        })

        // Roles del usuario: involvementRole → businessTenantPublicId (scope)
        const rolesMap: Record<string, string> = {}
        for (const t of tenants) {
          if (t.involvementRole && t.businessTenantPublicId) {
            rolesMap[t.involvementRole] = t.businessTenantPublicId
          }
        }
        store.setRolesMap(rolesMap)

        store.setContextProducts(ctx.products ?? [])

        console.info(
          `[Bootstrap] OK — ksecId=${di.id}` +
          ` isSuper=${accessRes.data.isKashioSuper}` +
          ` access=${accessRes.data.totalAccess}` +
          ` tenant=${firstTenant?.businessTenantPublicId ?? 'none'}` +
          ` role=${firstTenant?.involvementRole ?? 'none'}`,
        )

        // ── Step 4: Sync products (solo lectura, best-effort) ────────────────
        const partyRoleId  = di.subjectPartyRolePublicId ?? di.partyRoleIdentified?.[0]
        const orgPublicId  = firstTenant?.businessTenantPublicId ?? ''
        if (partyRoleId && orgPublicId) {
          try {
            const syncRes = await syncPartyRoleProducts(partyRoleId, orgPublicId)
            console.info(
              `[Bootstrap] syncPartyRoleProducts OK — partyRoleId=${partyRoleId}` +
              ` org=${orgPublicId}` +
              ` productCodes=${syncRes.data.kbrmProductCodes.join(',')}` +
              ` productsFound=${syncRes.data.productsFound.length}`,
            )
          } catch (syncErr) {
            console.warn('[Bootstrap] syncPartyRoleProducts failed (best-effort):', syncErr)
          }
        }
      } catch (err) {
        console.error('[Bootstrap] Failed:', err)
        // Mark as loaded so the UI doesn't hang — access.map stays empty
        // which means the user will see only always-visible items.
        store.setAccessMap({}, true)
      } finally {
        runningRef.current = false
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return { isBootstrapped: store.access.loaded, bootstrap }
}
